'use client'

import { create } from 'zustand'
import { getMemberships } from '@/lib/api'
import type { MembershipsInfo } from '@/lib/api'

interface MembershipsState {
  memberships: MembershipsInfo | null
  isLoading: boolean
  error: Error | null
  inFlight: Promise<void> | null
  /** When the last successful answer landed; a fresh one is reused instead of fetched again. */
  fetchedAt: number
  fetch: () => Promise<void>
  forceFetch: () => Promise<void>
  clear: () => void
  setReady: () => void
}

/**
 * Shared store for cheap org/team membership data. This backs surfaces that need
 * role or team identity but not billing balances or subscription reconciliation.
 */
/** Every surface that reads memberships asks for them on mount; within this window they share one answer. */
const FRESH_MS = 30_000

export const useMembershipsStore = create<MembershipsState>((set, get) => {
  let counter = 0

  const startFetch = ({ showLoading = false }: { showLoading?: boolean } = {}): Promise<void> => {
    const id = ++counter
    const promise = (async () => {
      try {
        const info = await getMemberships()
        if (id !== counter) return
        set({ memberships: info, error: null, isLoading: false, fetchedAt: Date.now() })
      } catch (err) {
        if (id !== counter) return
        set({
          error: err instanceof Error ? err : new Error('Failed to fetch memberships'),
          isLoading: false,
        })
      } finally {
        if (id === counter) set({ inFlight: null })
      }
    })()
    set({ inFlight: promise, ...(showLoading ? { isLoading: true } : {}) })
    return promise
  }

  return {
    memberships: null,
    isLoading: true,
    error: null,
    inFlight: null,
    fetchedAt: 0,

    fetch: () => {
      const { inFlight, memberships, fetchedAt } = get()
      if (inFlight) return inFlight
      if (memberships && Date.now() - fetchedAt < FRESH_MS) return Promise.resolve()
      return startFetch()
    },

    forceFetch: () => startFetch({ showLoading: true }),

    clear: () => {
      counter += 1
      set({ memberships: null, error: null, isLoading: false, inFlight: null, fetchedAt: 0 })
    },

    setReady: () => set({ isLoading: false }),
  }
})
